import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  sendEmailVerification
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, googleProvider, firestore } from '@config/firebase';
import { legalConfig } from '../legal/legalConfig';
import { calculateAge } from '../utils/age';

type UserRole = 'roommate_seeker' | 'landlord' | null;

interface SignupOptions {
  marketingOptIn?: boolean;
  dateOfBirth?: string;
}

interface StoredProfile {
  role: UserRole;
  profileComplete: boolean;
}

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userRole: UserRole;
  profileComplete: boolean;
  loading: boolean;
  signup: (email: string, password: string, role: UserRole, options?: SignupOptions) => Promise<void>;
  signupWithGoogle: (role: Exclude<UserRole, null>, options?: SignupOptions) => Promise<Exclude<UserRole, null>>;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  /** Re-read role + completeness from Firestore (e.g. after finishing onboarding). */
  refreshProfile: () => Promise<void>;
  /** Re-send the Firebase email-verification link to the current user. */
  resendVerification: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [profileComplete, setProfileComplete] = useState(false);
  const [loading, setLoading] = useState(true);

  const signup = async (
    email: string,
    password: string,
    role: UserRole,
    options?: SignupOptions
  ) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Fire off the verification email; don't fail signup if it can't send.
    try {
      await sendEmailVerification(userCredential.user);
    } catch (err) {
      console.error('Failed to send verification email:', err);
    }

    // Store user in appropriate collection based on role
    const collection = role === 'roommate_seeker' ? 'users' : 'landlords';
    await setDoc(doc(firestore, collection, userCredential.user.uid), {
      id: userCredential.user.uid,
      email,
      role,
      createdAt: new Date(),
      isActive: true,
      profileComplete: false,
      termsAcceptedAt: new Date(),
      termsVersion: legalConfig.documentsVersion,
      marketingOptIn: options?.marketingOptIn ?? false,
      ...(options?.dateOfBirth
        ? { dateOfBirth: options.dateOfBirth, age: calculateAge(options.dateOfBirth) }
        : {}),
    });

    setUserRole(role);
    setProfileComplete(false);
  };

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const getStoredProfile = async (userId: string): Promise<StoredProfile> => {
    const userDoc = await getDoc(doc(firestore, 'users', userId));
    if (userDoc.exists()) {
      return { role: 'roommate_seeker', profileComplete: userDoc.data().profileComplete === true };
    }

    const landlordDoc = await getDoc(doc(firestore, 'landlords', userId));
    if (landlordDoc.exists()) {
      return { role: 'landlord', profileComplete: landlordDoc.data().profileComplete === true };
    }

    return { role: null, profileComplete: false };
  };

  const refreshProfile = async () => {
    if (!auth.currentUser) return;
    const { role, profileComplete: complete } = await getStoredProfile(auth.currentUser.uid);
    setUserRole(role);
    setProfileComplete(complete);
  };

  const resendVerification = async () => {
    if (!auth.currentUser) throw new Error('You need to be signed in to verify your email.');
    await sendEmailVerification(auth.currentUser);
  };

  const signupWithGoogle = async (role: Exclude<UserRole, null>, options?: SignupOptions) => {
    const credential = await signInWithPopup(auth, googleProvider);
    const existing = await getStoredProfile(credential.user.uid);
    if (existing.role) {
      setUserRole(existing.role);
      setProfileComplete(existing.profileComplete);
      return existing.role;
    }

    const collection = role === 'roommate_seeker' ? 'users' : 'landlords';
    await setDoc(doc(firestore, collection, credential.user.uid), {
      id: credential.user.uid,
      email: credential.user.email || '',
      role,
      createdAt: new Date(),
      isActive: true,
      profileComplete: false,
      termsAcceptedAt: new Date(),
      termsVersion: legalConfig.documentsVersion,
      marketingOptIn: options?.marketingOptIn ?? false,
      ...(options?.dateOfBirth
        ? { dateOfBirth: options.dateOfBirth, age: calculateAge(options.dateOfBirth) }
        : {}),
    });
    setUserRole(role);
    setProfileComplete(false);
    return role;
  };

  const loginWithGoogle = async () => {
    const credential = await signInWithPopup(auth, googleProvider);
    const { role, profileComplete: complete } = await getStoredProfile(credential.user.uid);
    if (!role) {
      await signOut(auth);
      throw new Error('No RoomZa profile exists for this Google account. Create an account first.');
    }
    setUserRole(role);
    setProfileComplete(complete);
  };

  const logout = async () => {
    await signOut(auth);
    setUserRole(null);
    setProfileComplete(false);
  };

  useEffect(() => {
    let authStateVersion = 0;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const version = ++authStateVersion;
      setLoading(true);
      setCurrentUser(user);

      void (async () => {
        try {
          const { role, profileComplete: complete } = user
            ? await getStoredProfile(user.uid)
            : { role: null, profileComplete: false };
          if (version === authStateVersion) {
            setUserRole(role);
            setProfileComplete(complete);
          }
        } catch (error) {
          console.error('Error loading account role:', error);
          if (version === authStateVersion) {
            setUserRole(null);
            setProfileComplete(false);
          }
        } finally {
          if (version === authStateVersion) setLoading(false);
        }
      })();
    });

    return () => {
      authStateVersion += 1;
      unsubscribe();
    };
  }, []);

  const value: AuthContextType = {
    currentUser,
    userRole,
    profileComplete,
    loading,
    signup,
    signupWithGoogle,
    login,
    loginWithGoogle,
    logout,
    refreshProfile,
    resendVerification,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
